import { Section, SectionTitle, CaseCard } from './shared';

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="bg-slate-950 border border-slate-700/60 rounded-lg p-4 text-sm text-cyan-300 font-mono overflow-x-auto leading-relaxed">
      <code>{code}</code>
    </pre>
  );
}

function ArticleImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="rounded-lg overflow-hidden border border-slate-700/50 my-4">
      <img src={src} alt={alt} className="w-full object-top" />
    </div>
  );
}

function Blockquote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="border-l-4 border-cyan-500/60 bg-cyan-500/5 rounded-r-lg pl-4 pr-4 py-3 text-slate-300 text-sm my-4">
      {children}
    </blockquote>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-200 my-4 flex gap-2">
      <span className="text-amber-400 shrink-0">💡</span>
      <span>{children}</span>
    </div>
  );
}

export function SocialMediaContent() {
  const socialMediaCases = [
    {
      icon: '📱',
      title: '小红书自动运营',
      description: '使用 OpenClaw 实现小红书账号的自动化运营，包括内容发布、互动回复、数据分析等',
      tags: ['小红书', '自动化', '内容运营'],
      difficulty: 'intermediate' as const,
      link: 'https://curl.qcloud.com/Jh5l6fWJ',
    },
  ];

  return (
    <div className="space-y-10">

      {/* Header */}
      <div className="rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-800/60 via-slate-900 to-slate-950 overflow-hidden">
        <div className="p-8">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium">社交媒体</span>
            <span className="px-2.5 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-medium">中级</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-300 text-xs font-medium">小红书自动化</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-300 text-xs font-medium">AI内容生成</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-300 text-xs font-medium">开源项目</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-300 text-xs font-medium">数字员工</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3 leading-snug">OpenClaw 实现小红书自动化发文：操作指南</h1>
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-6">
            <span className="flex items-center gap-1"><i className="ri-user-line"></i> Neolnfra</span>
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> 2026-03-03</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i> 11.2K 阅读</span>
          </div>
          <a
            href="https://cloud.tencent.com/developer/article/2633552"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/40 text-cyan-400 text-sm hover:bg-cyan-500/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            查看原文（腾讯云开发者社区）
          </a>
        </div>
      </div>

      {/* 前言 */}
      <Section>
        <SectionTitle>前言</SectionTitle>
        <div className="space-y-3 text-slate-300 text-sm leading-relaxed mt-4">
          <p>小红书自动化神器：<strong className="text-white">xiaohongshu-mcp</strong>，让AI一键帮你发笔记、搜内容、写评论，日更50篇不封号！</p>
          <p>MCP for 小红书 | 用 Claude / Cursor / OpenClaw 一句话自动运营小红书，<strong className="text-white">9.7k stars</strong> 开源项目。</p>
          <p>AI时代的数字员工：<strong className="text-white">xiaohongshu-mcp + 大模型 = 你的小红书24小时自动内容工厂。</strong></p>
        </div>
        <ArticleImage
          src="https://developer.qcloudimg.com/http-save/yehe-11941136/afc628a80148b23f8054fcfa952739d5.png"
          alt="xiaohongshu-mcp 项目介绍"
        />
      </Section>

      {/* 安装与配置 */}
      <Section>
        <SectionTitle>一、安装与配置步骤</SectionTitle>

        {/* 1. 文件介绍 */}
        <div className="mt-6 space-y-4">
          <h3 className="text-base font-semibold text-white">1、文件介绍</h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            直接从{' '}
            <a href="https://github.com/xpzouying/xiaohongshu-mcp/releases" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">GitHub Releases</a>
            {' '}下载对应平台的二进制文件：
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">主程序（MCP 服务）</div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span><code className="text-cyan-300 text-xs">xiaohongshu-mcp-darwin-arm64</code><span className="text-slate-500 text-xs">macOS Apple Silicon</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span><code className="text-cyan-300 text-xs">xiaohongshu-mcp-darwin-amd64</code><span className="text-slate-500 text-xs">macOS Intel</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span><code className="text-cyan-300 text-xs">xiaohongshu-mcp-windows-amd64.exe</code><span className="text-slate-500 text-xs">Windows x64</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span><code className="text-cyan-300 text-xs">xiaohongshu-mcp-linux-amd64</code><span className="text-slate-500 text-xs">Linux x64</span></li>
              </ul>
            </div>
            <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">登录工具</div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span><code className="text-emerald-300 text-xs">xiaohongshu-login-darwin-arm64</code><span className="text-slate-500 text-xs">macOS Apple Silicon</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span><code className="text-emerald-300 text-xs">xiaohongshu-login-darwin-amd64</code><span className="text-slate-500 text-xs">macOS Intel</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span><code className="text-emerald-300 text-xs">xiaohongshu-login-windows-amd64.exe</code><span className="text-slate-500 text-xs">Windows x64</span></li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span><code className="text-emerald-300 text-xs">xiaohongshu-login-linux-amd64</code><span className="text-slate-500 text-xs">Linux x64</span></li>
              </ul>
            </div>
          </div>
          <p className="text-slate-400 text-sm">每个压缩包包含：<code className="text-cyan-300 text-xs bg-slate-800 px-1.5 py-0.5 rounded">xiaohongshu-mcp-*</code> MCP 服务主程序 和 <code className="text-emerald-300 text-xs bg-slate-800 px-1.5 py-0.5 rounded">xiaohongshu-login-*</code> 登录工具</p>
          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/074cfb7e5fb09f4648049cc666b0d8fa.png"
            alt="GitHub Releases 下载页面"
          />
          <p className="text-slate-400 text-sm">由于 OpenClaw 目前不原生支持 MCP，官方推荐通过 <strong className="text-white">MCPorter</strong> 来调用 MCP 服务。</p>
          <TipBox>提示：MCPorter 并非调用 MCP 的最佳方案，使用过程中可能出现一些兼容性问题，请知悉。</TipBox>
        </div>

        {/* 2. 安装与配置步骤 */}
        <div className="mt-8 space-y-5">
          <h3 className="text-base font-semibold text-white">2、安装与配置步骤</h3>

          <div className="space-y-2">
            <p className="text-slate-400 text-sm">下载对应安装包，并解压到一个固定文件夹中，比如 <code className="text-cyan-300 text-xs bg-slate-800 px-1.5 py-0.5 rounded">D:\xiaohongshu-mcp\</code></p>
            <CodeBlock code={`1. 解压 xiaohongshu-mcp-windows-amd64.zip

2. 运行登录工具
./xiaohongshu-login-darwin-arm64

3. 启动 MCP 服务
./xiaohongshu-mcp-darwin-arm64`} />
          </div>

          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/2929d448796e89d4f108d81280f3048f.png"
            alt="启动 MCP 服务"
          />

          <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5 space-y-2 text-sm text-slate-300">
            <p><strong className="text-white">Windows 步骤：</strong></p>
            <ul className="space-y-1.5 pl-4">
              <li>• 打开命令提示符（CMD）</li>
              <li>• 进入目录：<code className="text-cyan-300 bg-slate-900 px-1.5 py-0.5 rounded text-xs">cd D:\xiaohongshu-mcp</code></li>
              <li>• 执行：<code className="text-cyan-300 bg-slate-900 px-1.5 py-0.5 rounded text-xs">xiaohongshu-login-windows-amd64.exe</code></li>
              <li>• 程序会自动打开 Chrome 浏览器，用小红书 App 扫码登录</li>
              <li>• 登录成功后会自动把 cookie 保存到本地当前目录</li>
            </ul>
          </div>

          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/15d6cf88b5e0f9becfe41ebd551acb5e.png"
            alt="登录工具界面"
          />

          <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5 space-y-2 text-sm text-slate-300">
            <p><strong className="text-white">启动 MCP 服务：</strong></p>
            <ul className="space-y-1.5 pl-4">
              <li>• 在同一个命令窗口执行：<code className="text-cyan-300 bg-slate-900 px-1.5 py-0.5 rounded text-xs">xiaohongshu-mcp-windows-amd64.exe</code></li>
              <li>• 看到类似 <code className="text-emerald-300 bg-slate-900 px-1.5 py-0.5 rounded text-xs">"Server running at http://localhost:18060"</code> 的提示就启动成功了</li>
              <li>• 默认端口 18060，<strong className="text-white">保持这个窗口不要关</strong></li>
            </ul>
          </div>

          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/2c185bdffef8aece7d9c6ed663e38d63.png"
            alt="MCP 服务启动成功"
          />

          <div className="space-y-2">
            <p className="text-slate-400 text-sm">OpenClaw 目前不原生支持 MCP，官方推荐用 MCPorter 作为中间层。在 OpenClaw 的任意对话框（Control UI、飞书、Telegram 等）一次性粘贴下面三行，让 AI 自动帮你安装和配置：</p>
            <CodeBlock code={`npm i -g mcporter
npx mcporter config add xiaohongshu-mcp http://localhost:18060/mcp
npx mcporter list xiaohongshu-mcp`} />
          </div>

          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/860a9315fe7a5399a89aac7cb59e5830.gif"
            alt="MCPorter 配置动图演示"
          />
        </div>

        {/* 3. 配置验证 */}
        <div className="mt-8 space-y-4">
          <h3 className="text-base font-semibold text-white">3、配置验证</h3>
          <p className="text-slate-400 text-sm">配置完后，测试一下：在对话框输入</p>
          <Blockquote>
            <code>查看 xiaohongshu-mcp 是否连接成功，能自动发小红书笔记吗？</code>
          </Blockquote>
          <ArticleImage
            src="https://developer.qcloudimg.com/http-save/yehe-11941136/ba2eb7a4cfedca70553904abc9a9b5ec.png"
            alt="配置验证截图"
          />
          <p className="text-slate-400 text-sm">如果返回工具列表（比如 <code className="text-cyan-300 bg-slate-800 px-1.5 py-0.5 rounded text-xs">publish_content</code>、<code className="text-cyan-300 bg-slate-800 px-1.5 py-0.5 rounded text-xs">check_login_status</code> 等），说明接入成功。完成上述步骤后，即可在 OpenClaw 中通过自然语言调用 xiaohongshu-mcp 的所有功能。</p>
        </div>
      </Section>

      {/* 使用示例 */}
      <Section>
        <SectionTitle>二、使用示例</SectionTitle>
        <div className="mt-4 space-y-4">
          <p className="text-slate-400 text-sm">连接成功后，可使用以下 MCP 工具：</p>

          <div className="grid md:grid-cols-2 gap-3">
            {[
              { name: 'check_login_status', desc: '检查小红书登录状态（无参数）', color: 'border-cyan-500/20 bg-cyan-500/5' },
              { name: 'publish_content', desc: '发布图文内容（必需：title, content, images）', color: 'border-emerald-500/20 bg-emerald-500/5' },
              { name: 'publish_with_video', desc: '发布视频内容（必需：title, content, video）', color: 'border-violet-500/20 bg-violet-500/5' },
              { name: 'list_feeds', desc: '获取小红书首页推荐列表（无参数）', color: 'border-orange-500/20 bg-orange-500/5' },
              { name: 'search_feeds', desc: '搜索小红书内容（需要：keyword）', color: 'border-pink-500/20 bg-pink-500/5' },
              { name: 'get_feed_detail', desc: '获取帖子详情（需要：feed_id, xsec_token）', color: 'border-yellow-500/20 bg-yellow-500/5' },
              { name: 'post_comment_to_feed', desc: '发表评论（需要：feed_id, xsec_token, content）', color: 'border-teal-500/20 bg-teal-500/5' },
              { name: 'user_profile', desc: '获取用户个人主页信息（需要：user_id, xsec_token）', color: 'border-indigo-500/20 bg-indigo-500/5' },
            ].map((tool) => (
              <div key={tool.name} className={`rounded-lg border ${tool.color} p-3 flex flex-col gap-1`}>
                <code className="text-xs font-mono text-white font-semibold">{tool.name}</code>
                <span className="text-xs text-slate-400">{tool.desc}</span>
              </div>
            ))}
          </div>

          {/* 示例1 */}
          <div className="mt-6 space-y-3">
            <h3 className="text-base font-semibold text-white">示例 1：简单图文发布</h3>
            <Blockquote>
              <p>帮我写一篇帖子发布到小红书上，</p>
              <p>配图为：本地 D:\Users\LING\Desktop\logo\1a</p>
              <p>内容：小红书发文成功 使用 xiaohongshu-mcp 进行发布。</p>
            </Blockquote>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">测试结果</p>
            <ArticleImage
              src="https://developer.qcloudimg.com/http-save/yehe-11941136/c5a1168f8a2be581cb53067bd02a7e11.png"
              alt="示例1 - 图文发布结果1"
            />
            <ArticleImage
              src="https://developer.qcloudimg.com/http-save/yehe-11941136/f10f1ff7cdacf1e856fb272146eabe55.png"
              alt="示例1 - 图文发布结果2"
            />
          </div>

          {/* 示例2 */}
          <div className="mt-6 space-y-3">
            <h3 className="text-base font-semibold text-white">示例 2：使用本地图片发布</h3>
            <Blockquote>
              帮我写一篇关于春天的帖子发布到小红书上，使用这些本地图片：本地 D:\Users\LING\Desktop\logo\ct 使用 xiaohongshu-mcp 进行发布。
            </Blockquote>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">测试结果</p>
            <ArticleImage
              src="https://developer.qcloudimg.com/http-save/yehe-11941136/2276546c9cd09a78a6cae42b1268eac3.png"
              alt="示例2 - 本地图片发布结果1"
            />
            <ArticleImage
              src="https://developer.qcloudimg.com/http-save/yehe-11941136/7b278bbabe6c36cb906dfdc9517f844f.png"
              alt="示例2 - 本地图片发布结果2"
            />
          </div>
        </div>
      </Section>

      {/* 总结 */}
      <Section>
        <SectionTitle>三、总结</SectionTitle>
        <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 space-y-4 text-sm text-slate-300 leading-relaxed">
          <p>
            总的来说，<strong className="text-white">xiaohongshu-mcp</strong> 是目前最成熟、最活跃的小红书 AI 自动化方案。它把复杂的账号操作标准化成 MCP 工具，让 Claude、Cursor、OpenClaw 等 AI 助手真正成为你的"内容数字员工"——从登录到发布，只需一句话，就能完成过去需要手动几十分钟的工作。
          </p>
          <p>
            项目开源免费、本地运行、社区持续更新（<strong className="text-white">9.7k+ stars</strong> 不是白来的），上手门槛低，但潜力巨大：日更、批量测试、互动自动化、甚至多平台扩展，都变得触手可及。
          </p>
          <div className="flex items-center gap-3 pt-2">
            <a
              href="https://github.com/xpzouying/xiaohongshu-mcp?tab=readme-ov-file"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-200 text-sm hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-github-line"></i>
              GitHub 项目地址
            </a>
            <a
              href="https://cloud.tencent.com/developer/article/2633552"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/40 text-cyan-400 text-sm hover:bg-cyan-500/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              查看原文
            </a>
          </div>
        </div>
      </Section>

      {/* 更多社交媒体案例 */}
      <Section>
        <SectionTitle>更多社交媒体案例</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
          {socialMediaCases.map((caseItem, index) => (
            <CaseCard key={index} {...caseItem} />
          ))}
        </div>
      </Section>

    </div>
  );
}